import React, { useEffect, useState } from "react";
import { StyleSheet, css } from "aphrodite";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ViewListIcon from "@mui/icons-material/ViewList";
import Dashboard from "../../../services/api/dashboard";

// Component Styles
const styles = StyleSheet.create({
  card: {
    marginTop: "15px",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    width: "100%",
    overflow: "hidden",
    transition: "all 0.3s ease",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
    },
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "16px 20px 12px 20px",
    borderBottom: "1px solid #f0f0f0",
    "@media (max-width: 768px)": {
      padding: "12px 16px 8px 16px",
    },
  },
  titleSection: {
    flex: 1,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 4,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: "#1a1a1a",
    margin: 0,
    "@media (max-width: 768px)": {
      fontSize: 16,
    },
  },
  subtitle: {
    fontSize: 12,
    color: "#666",
    margin: 0,
    lineHeight: 1.4,
  },
  actionIcons: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 2,
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 13,
    "@media (max-width: 768px)": {
      fontSize: 11,
    },
  },
  monthHeaderRow: {
    background: "#1a237e",
  },
  monthHeader: {
    padding: "8px 4px",
    fontSize: 13,
    fontWeight: 700,
    color: "#fff",
    textAlign: "center",
    borderRight: "1px solid #303f9f",
    "@media (max-width: 768px)": {
      fontSize: 11,
      padding: "6px 2px",
    },
  },
  columnHeaderRow: {
    background: "#1a237e",
  },
  columnHeader: {
    padding: "6px 4px",
    fontSize: 11,
    fontWeight: 600,
    color: "#fff",
    textAlign: "center",
    borderRight: "1px solid #303f9f",
    borderTop: "1px solid #303f9f",
    "@media (max-width: 768px)": {
      fontSize: 10,
      padding: "4px 2px",
    },
  },
  zoneNameHeader: {
    textAlign: "left",
    paddingLeft: 12,
    borderRight: "1px solid #303f9f",
    "@media (max-width: 768px)": {
      paddingLeft: 8,
    },
  },
  dataRow: {
    ":hover": {
      backgroundColor: "#f8f9fa",
    },
  },
  cell: {
    padding: "6px 4px",
    textAlign: "center",
    borderBottom: "1px solid #f0f0f0",
    borderRight: "1px solid #f0f0f0",
    color: "#333",
    fontSize: 12,
    "@media (max-width: 768px)": {
      fontSize: 10,
      padding: "4px 2px",
    },
  },
  zoneNameCell: {
    textAlign: "left",
    paddingLeft: 12,
    fontWeight: 500,
    color: "#555",
    "@media (max-width: 768px)": {
      paddingLeft: 8,
    },
  },
  arrowCell: {
    color: "#999",
    fontSize: 11,
    fontWeight: 400,
  },
  totalRow: {
    backgroundColor: "#f8f9fa",
    fontWeight: 600,
    borderTop: "2px solid #e0e0e0",
  },
  // Loading and Error states
  loadingContainer: {
    padding: "40px 20px",
    textAlign: "center",
    color: "#666",
    fontSize: 14,
  },
  errorContainer: {
    padding: "40px 20px",
    textAlign: "center",
    color: "#d32f2f",
    fontSize: 14,
    backgroundColor: "#ffebee",
    margin: "20px",
    borderRadius: 8,
  },
});

const getLastMonths = (count = 3) => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const today = new Date();
  const months = Array.from({ length: count }, (_, idx) => {
    const d = new Date(
      today.getFullYear(),
      today.getMonth() - (count - idx),
      1
    );
    return monthNames[d.getMonth()];
  });

  return months;
};

function FriendToMemberTrend() {
  const [tableData, setTableData] = useState({ rows: [], total: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const lastMonths = getLastMonths();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await Dashboard.getFriendToMemberTrends();
        // console.log("Full API Response:", response);
        // console.log("Response.data:", response.data);

        // Check if response exists
        if (!response) {
          throw new Error("No response received");
        }

        // Handle different response structures
        let apiData = null;

        if (response.data && response.data.data) {
          // Structure: { data: { data: [...] } }
          apiData = response.data.data;
          console.log("Using response.data.data:", apiData);
        } else if (response.data && Array.isArray(response.data)) {
          // Structure: { data: [...] }
          apiData = response.data;
          console.log("Using response.data:", apiData);
        } else if (Array.isArray(response)) {
          // Structure: [...]
          apiData = response;
          // console.log("Using direct response:", apiData);
        } else {
          console.error("Unexpected response structure:", response);
          throw new Error("Unable to find data array in response");
        }

        // Validate that apiData is an array
        if (!Array.isArray(apiData)) {
          console.error("Data is not an array:", typeof apiData, apiData);
          throw new Error(
            `Expected data to be an array, got ${typeof apiData}`
          );
        }

        // console.log("Final API Data:", apiData);

        // Convert API data into required format
        let rows = apiData.map((item, index) => ({
          id: item.zoneId || index + 1, // Use zoneId as unique identifier
          zoneName: item.zoneName?.trim() || "Unknown Zone",
          month1Friend: item.month1Friend || 0,
          month1Member: item.month1Member || 0,
          month2Friend: item.month2Friend || 0,
          month2Member: item.month2Member || 0,
          month3Friend: item.month3Friend || 0,
          month3Member: item.month3Member || 0,
        }));

        // ✅ Sort alphabetically by zoneName (A → Z)
        rows = rows.sort((a, b) =>
          a.zoneName.localeCompare(b.zoneName, "en", { sensitivity: "base" })
        );

        // console.log("Processed rows:", rows);

        // Calculate totals
        const total = {
          zoneName: "TOTAL",
          month1Friend: rows.reduce(
            (sum, r) => sum + (Number(r.month1Friend) || 0),
            0
          ),
          month1Member: rows.reduce(
            (sum, r) => sum + (Number(r.month1Member) || 0),
            0
          ),
          month2Friend: rows.reduce(
            (sum, r) => sum + (Number(r.month2Friend) || 0),
            0
          ),
          month2Member: rows.reduce(
            (sum, r) => sum + (Number(r.month2Member) || 0),
            0
          ),
          month3Friend: rows.reduce(
            (sum, r) => sum + (Number(r.month3Friend) || 0),
            0
          ),
          month3Member: rows.reduce(
            (sum, r) => sum + (Number(r.month3Member) || 0),
            0
          ),
        };

        // console.log("Calculated total:", total);

        setTableData({ rows, total });
      } catch (err) {
        console.error("Error fetching trends:", err);
        // setError(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className={css(styles.card)}>
        <div className={css(styles.header)}>
          <div className={css(styles.titleSection)}>
            <div className={css(styles.titleRow)}>
              <h3 className={css(styles.title)}>Friend to Member Trend</h3>
              {/* <InfoOutlinedIcon fontSize="small" style={{ color: "#999" }} /> */}
            </div>
            <p className={css(styles.subtitle)}>
              Discussion Meetings (Last 3 month report)
            </p>
          </div>
          <div className={css(styles.actionIcons)}>
            <ViewListIcon fontSize="small" style={{ color: "#999" }} />
            <MoreVertIcon fontSize="small" style={{ color: "#999" }} />
          </div>
        </div>
        <div className={css(styles.loadingContainer)}>
          Loading Friend to Member Trends...
        </div>
      </div>
    );
  }

  // Error state with debugging info
  if (error) {
    return (
      <div className={css(styles.card)}>
        <div className={css(styles.header)}>
          <div className={css(styles.titleSection)}>
            <div className={css(styles.titleRow)}>
              <h3 className={css(styles.title)}>Friend to Member Trend</h3>
              {/* <InfoOutlinedIcon fontSize="small" style={{ color: "#999" }} /> */}
            </div>
            <p className={css(styles.subtitle)}>
              Discussion Meetings (Last 3 month report)
            </p>
          </div>
          <div className={css(styles.actionIcons)}>
            <ViewListIcon fontSize="small" style={{ color: "#999" }} />
            <MoreVertIcon fontSize="small" style={{ color: "#999" }} />
          </div>
        </div>
        <div className={css(styles.errorContainer)}>
          <strong>Error: {error}</strong>
          <br />
          <br />
          <small>
            Please check browser console for detailed response structure.
            <br />
            Common solutions:
            <br />
            1. Check API endpoint URL
            <br />
            2. Verify API response format
            <br />
            3. Check network connectivity
          </small>
        </div>
      </div>
    );
  }

  // Empty data state
  if (!tableData.rows || tableData.rows.length === 0) {
    return (
      <div className={css(styles.card)}>
        <div className={css(styles.header)}>
          <div className={css(styles.titleSection)}>
            <div className={css(styles.titleRow)}>
              <h3 className={css(styles.title)}>Friend to Member Trend</h3>
              {/* <InfoOutlinedIcon fontSize="small" style={{ color: "#999" }} /> */}
            </div>
            <p className={css(styles.subtitle)}>
              Discussion Meetings (Last 3 month report)
            </p>
          </div>
          <div className={css(styles.actionIcons)}>
            <ViewListIcon fontSize="small" style={{ color: "#999" }} />
            <MoreVertIcon fontSize="small" style={{ color: "#999" }} />
          </div>
        </div>
        <div className={css(styles.loadingContainer)}>No data available</div>
      </div>
    );
  }

  return (
    <div className={css(styles.card)}>
      <div className={css(styles.header)}>
        <div className={css(styles.titleSection)}>
          <div className={css(styles.titleRow)}>
            <h3 className={css(styles.title)}>Friend to Member Trend</h3>
            {/* <InfoOutlinedIcon fontSize="small" style={{ color: "#999" }} /> */}
          </div>
          <p className={css(styles.subtitle)}>
            Discussion Meetings (Last 3 month report)
          </p>
        </div>
        <div className={css(styles.actionIcons)}>
          <ViewListIcon fontSize="small" style={{ color: "#999" }} />
          <MoreVertIcon fontSize="small" style={{ color: "#999" }} />
        </div>
      </div>

      <div className={css(styles.tableContainer)}>
        <table className={css(styles.table)}>
          <thead>
            <tr className={css(styles.monthHeaderRow)}>
              <th className={css(styles.monthHeader, styles.zoneNameHeader)}>
                Unit Name
              </th>
              {lastMonths.map((month) => (
                <th key={month} colSpan="3" className={css(styles.monthHeader)}>
                  {month}
                </th>
              ))}
            </tr>
            <tr className={css(styles.columnHeaderRow)}>
              <th className={css(styles.columnHeader)}> </th>
              <th className={css(styles.columnHeader)}>Friend</th>
              <th className={css(styles.columnHeader)}>→</th>
              <th className={css(styles.columnHeader)}>Member</th>
              <th className={css(styles.columnHeader)}>Friend</th>
              <th className={css(styles.columnHeader)}>→</th>
              <th className={css(styles.columnHeader)}>Member</th>
              <th className={css(styles.columnHeader)}>Friend</th>
              <th className={css(styles.columnHeader)}>→</th>
              <th className={css(styles.columnHeader)}>Member</th>
            </tr>
          </thead>
          <tbody>
            {tableData.rows.map((row) => (
              <tr key={row.id} className={css(styles.dataRow)}>
                <td className={css(styles.cell, styles.zoneNameCell)}>
                  {row.zoneName}
                </td>
                <td className={css(styles.cell)}>{row.month3Friend}</td>
                <td className={css(styles.cell, styles.arrowCell)}>→</td>
                <td className={css(styles.cell)}>{row.month3Member}</td>
                <td className={css(styles.cell)}>{row.month2Friend}</td>
                <td className={css(styles.cell, styles.arrowCell)}>→</td>
                <td className={css(styles.cell)}>{row.month2Member}</td>
                <td className={css(styles.cell)}>{row.month1Friend}</td>
                <td className={css(styles.cell, styles.arrowCell)}>→</td>
                <td className={css(styles.cell)}>{row.month1Member}</td>
              </tr>
            ))}
            {/* Total Row */}
            <tr className={css(styles.dataRow, styles.totalRow)}>
              <td className={css(styles.cell, styles.zoneNameCell)}>
                {tableData.total.zoneName}
              </td>
              <td className={css(styles.cell)}>
                {tableData.total.month3Friend}
              </td>
              <td className={css(styles.cell, styles.arrowCell)}>→</td>

              <td className={css(styles.cell)}>
                {tableData.total.month3Member}
              </td>
              <td className={css(styles.cell)}>
                {tableData.total.month2Friend}
              </td>
              <td className={css(styles.cell, styles.arrowCell)}>→</td>

              <td className={css(styles.cell)}>
                {tableData.total.month2Member}
              </td>

              <td className={css(styles.cell)}>
                {tableData.total.month1Friend}
              </td>
              <td className={css(styles.cell, styles.arrowCell)}>→</td>
              <td className={css(styles.cell)}>
                {tableData.total.month1Member}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default FriendToMemberTrend;
